import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading } from './ui/UI';
import { Linkedin, ExternalLink } from 'lucide-react';
const SEAN_PORTRAIT = 'https://cdn.magicpatterns.com/uploads/kB2BfftmcjPffkK7NuximY/Screenshot_2025-02-21_at_6.47.22_PM.png';
const sectors = ['Cybersecurity', 'Telecom', 'CPaaS', 'SaaS', 'Cloud', 'Open source', 'AI'];
const career = [{
  n: '01',
  year: '2004',
  company: 'Periscan',
  role: 'Founder & CEO',
  body: 'Pioneered continuous security validation before the market had language for ASV, AEV, CTEM, BAS, EASM, or exposure validation. Built an always-on "hacker\'s-eye view" platform. Early PCI compliance scanning vendor. Acquired by Catbird Networks.',
  chips: ['Continuous validation', 'Outside-in', 'Hosted SaaS', 'PCI']
}, {
  n: '02',
  year: 'Post-Periscan',
  company: 'Barracuda Networks',
  role: 'First PM · VP Strategy · Director, New Products',
  body: 'First product manager. Later VP of Strategy and Director of New Products. Helped launch products that grew to $200M+ in ARR.',
  chips: ['$200M+ ARR', 'IPO messaging', 'New products']
}, {
  n: '03',
  year: 'CPaaS era',
  company: 'SignalWire',
  role: 'Co-Founder · CMO · COO',
  body: 'Co-founded the CPaaS company and led marketing and operations. Helped build the company from scratch and raise $42M+ from leading Silicon Valley investors.',
  chips: ['$42M+ raised', 'CPaaS', 'Open source']
}, {
  n: '04',
  year: 'Recent',
  company: 'Advisory & AyeAI',
  role: 'Founder · Advisor',
  body: 'Built and advised companies across cybersecurity, telecom, CPaaS, SaaS, cloud, open source, and AI — including recent work with international cybersecurity companies entering the U.S. market.',
  chips: ['AI infrastructure', 'Cyber advisory', 'US market entry']
}, {
  n: '05',
  year: '2026',
  company: 'Periscan',
  role: 'Returning Founder & CEO',
  body: 'Bringing Periscan back for the validation era — cloud, identity, SaaS, AI apps, continuous exposure, and evidence-driven security operations.',
  chips: ['Cloud + identity', 'AI app validation', 'CTEM', 'Evidence-driven'],
  active: true
}];
export function FounderProfile() {
  return <section id="founder" className="relative w-full py-32 border-t border-white/5" data-id="element-583">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-584">
        <SectionLabel number="02" label="Founder & CEO" className="mb-8" data-id="element-585" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-586">
          <div className="lg:col-span-8" data-id="element-587">
            <SectionHeading size="xl" data-id="element-588">
              Sean Heiney.
              <br data-id="element-589" />
              <span className="text-white/40" data-id="element-590">Founder & CEO.</span>
            </SectionHeading>
          </div>
          <div className="lg:col-span-4 lg:pt-4" data-id="element-591">
            <p className="text-base text-white/60 leading-relaxed" data-id="element-592">
              One of the earliest builders in continuous security validation —
              now returning to the category he helped start.
            </p>
          </div>
        </div>

        {/* ── Main bio (canonical) ──────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-24" data-id="element-593">
          {/* Portrait */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-5" data-id="element-594">
            <div className="lg:sticky lg:top-32" data-id="element-595">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black" data-id="element-596">
                <div className="relative aspect-[4/5]" data-id="element-597">
                  <img src={SEAN_PORTRAIT} alt="Sean Heiney, Founder & CEO of Periscan" className="absolute inset-0 w-full h-full object-contain object-center" data-id="element-598" />
                  <div className="absolute inset-0 pointer-events-none" style={{
                  background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.7) 100%)'
                }} data-id="element-599" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-[#1a8fd4]/20 rounded-2xl pointer-events-none" data-id="element-600" />

                  <div className="absolute bottom-5 left-5 right-5" data-id="element-601">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[#1a8fd4] mb-1" data-id="element-602">
                      Founder & CEO
                    </p>
                    <p className="text-xl font-semibold text-white tracking-tight" data-id="element-603">
                      Sean Heiney
                    </p>
                  </div>

                  <div className="absolute top-5 right-5 text-right" data-id="element-604">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/80" data-id="element-605">
                      Building · 2026
                    </span>
                  </div>
                </div>
              </div>

              {/* Stat tiles */}
              <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-606">
                <div className="p-3 rounded-lg bg-[#070a0e] border border-white/[0.06] text-center" data-id="element-607">
                  <p className="text-xl font-semibold text-white" data-id="element-608">2004</p>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-1" data-id="element-609">
                    Founded Periscan
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-[#070a0e] border border-white/[0.06] text-center" data-id="element-610">
                  <p className="text-xl font-semibold text-white" data-id="element-611">$200M+</p>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-1" data-id="element-612">
                    ARR launched
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-[#070a0e] border border-white/[0.06] text-center" data-id="element-613">
                  <p className="text-xl font-semibold text-white" data-id="element-614">$42M+</p>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-1" data-id="element-615">
                    Capital raised
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Canonical bio copy */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="lg:col-span-7 space-y-6" data-id="element-616">
            <p className="text-lg md:text-xl text-white/85 leading-relaxed" data-id="element-617">
              Sean Heiney founded Periscan in 2004, pioneering continuous
              security validation before the market had language for{' '}
              <span className="text-white" data-id="element-618">
                ASV, AEV, CTEM, BAS, EASM, or exposure validation.
              </span>
            </p>

            <p className="text-base text-white/65 leading-relaxed" data-id="element-619">
              The original Periscan gave customers an always-on hacker's-eye
              view of their networks, helped organizations continuously validate
              external exposure, and became one of the earliest PCI compliance
              scanning vendors. The company was later acquired by Catbird
              Networks.
            </p>

            <p className="text-base text-white/65 leading-relaxed" data-id="element-620">
              Sean has spent his career turning complex infrastructure into
              products, categories, and revenue. He was the{' '}
              <span className="text-white" data-id="element-621">
                first product manager at Barracuda Networks,
              </span>{' '}
              later serving as VP of Strategy and Director of New Products,
              where he helped launch products that grew to{' '}
              <span className="text-white" data-id="element-622">$200M+ in ARR.</span> He later
              co-founded <span className="text-white" data-id="element-623">SignalWire</span> and
              served as Founder, CMO, and COO, helping build the company from
              scratch and raise{' '}
              <span className="text-white" data-id="element-624">
                $42M+ from leading Silicon Valley investors.
              </span>
            </p>

            <p className="text-base text-white/65 leading-relaxed" data-id="element-625">
              He has also built and advised companies across cybersecurity,
              telecom, CPaaS, SaaS, cloud, open source, and AI, including recent
              work with international cybersecurity companies entering the U.S.
              market.
            </p>

            <div className="flex flex-wrap gap-2 pt-2" data-id="element-626">
              {sectors.map(s => <span key={s} className="px-3 py-1.5 text-xs text-white/70 bg-white/[0.03] border border-white/10 rounded-full" data-id="element-627">
                  {s}
                </span>)}
            </div>

            {/* Pull quote */}
            <blockquote className="mt-10 pl-6 border-l-2 border-[#1a8fd4]/60" data-id="element-628">
              <p className="text-2xl md:text-3xl text-white/95 leading-snug tracking-tight font-light" data-id="element-629">
                In 2026, Sean is bringing Periscan back for the validation era.
              </p>
              <p className="mt-4 font-mono text-sm text-[#1a8fd4]" data-id="element-630">
                Same founder. Same thesis. A much bigger surface.
              </p>
            </blockquote>

            {/* External links */}
            <div className="pt-6 flex flex-wrap items-center gap-3" data-id="element-631">
              <a href="https://www.linkedin.com/in/seanheiney/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/80 hover:text-white transition-colors" data-id="element-632">
                <Linkedin className="w-3.5 h-3.5" data-id="element-633" />
                LinkedIn
              </a>
              <a href="mailto:sean@periscan.com" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/80 hover:text-white transition-colors" data-id="element-634">
                <ExternalLink className="w-3.5 h-3.5" data-id="element-635" />
                sean@periscan.com
              </a>
            </div>
          </motion.div>
        </div>

        {/* ── Career stops rail ─────────────────────────────────────── */}
        <div className="mb-12" data-id="element-636">
          <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-3" data-id="element-637">
            Career stops
          </p>
          <h3 className="text-2xl md:text-3xl font-semibold text-white tracking-tight" data-id="element-638">
            Two decades of building.
            <span className="text-white/40" data-id="element-639"> Five chapters.</span>
          </h3>
        </div>

        <div className="relative" data-id="element-640">
          <div className="absolute left-[1.875rem] top-2 bottom-2 w-px bg-gradient-to-b from-white/5 via-[#1a8fd4]/30 to-white/5 hidden md:block" data-id="element-641" />

          <div className="space-y-3" data-id="element-642">
            {career.map((c, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: 10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className="relative grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-start" data-id="element-643">
                <div className={`absolute left-[1.625rem] top-7 w-3.5 h-3.5 rounded-full hidden md:block ${c.active ? 'bg-[#1a8fd4] shadow-[0_0_12px_#1a8fd4]' : 'bg-[#1a8fd4]/30 border border-[#1a8fd4]/50'}`} data-id="element-644">
                  <span className="absolute inset-[3px] rounded-full bg-[#050608]" data-id="element-645" />
                </div>

                <div className="md:col-span-3 md:pl-16" data-id="element-646">
                  <p className={`font-mono text-[10px] uppercase tracking-widest mb-1 ${c.active ? 'text-[#1a8fd4]' : 'text-white/40'}`} data-id="element-647">
                    {c.n} · {c.year}
                  </p>
                  <p className="text-base font-semibold text-white tracking-tight leading-snug" data-id="element-648">
                    {c.company}
                  </p>
                </div>

                <div className={`md:col-span-9 p-6 rounded-xl border transition-colors ${c.active ? 'bg-gradient-to-br from-[#1a8fd4]/10 to-[#0a0d12] border-[#1a8fd4]/40' : 'bg-[#070a0e] border-white/[0.06] hover:border-white/15'}`} data-id="element-649">
                  <p className={`font-mono text-[10px] uppercase tracking-widest mb-3 ${c.active ? 'text-[#1a8fd4]' : 'text-white/50'}`} data-id="element-650">
                    {c.role}
                  </p>
                  <p className="text-sm text-white/70 leading-relaxed mb-4" data-id="element-651">
                    {c.body}
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-652">
                    {c.chips.map(chip => <span key={chip} className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-md border ${c.active ? 'text-[#1a8fd4] bg-[#1a8fd4]/10 border-[#1a8fd4]/20' : 'text-white/55 bg-white/[0.03] border-white/5'}`} data-id="element-653">
                        {chip}
                      </span>)}
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Closing */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-16 p-8 md:p-12 rounded-2xl bg-gradient-to-br from-[#1a8fd4]/10 to-transparent border border-[#1a8fd4]/20" data-id="element-654">
          <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-3" data-id="element-655">
            Full circle
          </p>
          <p className="text-2xl md:text-3xl text-white leading-snug tracking-tight max-w-3xl" data-id="element-656">
            Periscan is Sean returning to the category he helped start.
          </p>
        </motion.div>
      </div>
    </section>;
}